/**
 * Haptic Feedback System - THE ONESCENT DIAL
 * 
 * Provides tactile feedback on mobile devices using the Vibration API.
 * Gracefully degrades on unsupported devices.
 * 
 * PROPRIETARY NOTICE: THE ONESCENT DIAL
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 */

// Check if vibration is supported
export const isVibrationSupported = () => {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
};

// Check if user prefers reduced motion (respect accessibility)
const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Haptic patterns (duration in milliseconds)
const HAPTIC_PATTERNS = {
  // Light tap - for buttons, toggles
  light: [10],
  
  // Medium tap - for confirmations, selections
  medium: [25],
  
  // Heavy tap - for important actions
  heavy: [50],
  
  // Double tap - for toggle on/off
  double: [15, 50, 15],
  
  // Success pattern - for completed actions
  success: [10, 30, 10, 30, 50],
  
  // Error pattern - for failed actions
  error: [100, 50, 100],
  
  // Notification pattern - for alerts
  notification: [50, 100, 50],
  
  // Soft pulse - for ambient/sound toggle
  pulse: [5, 20, 5],
  
  // Selection - for picking items
  selection: [8],
};

// Global state for haptic preferences
let hapticEnabled = true;

/**
 * Initialize haptic system and load user preferences
 */
export const initHaptics = () => {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem('tod_haptic_enabled');
    hapticEnabled = saved !== 'false'; // Default to true
  }
};

/**
 * Enable or disable haptic feedback
 */
export const setHapticEnabled = (enabled) => {
  hapticEnabled = enabled;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('tod_haptic_enabled', String(enabled));
  }
};

/**
 * Check if haptics are currently enabled
 */
export const isHapticEnabled = () => hapticEnabled;

/**
 * Trigger haptic feedback
 * @param {string} type - Type of haptic feedback (light, medium, heavy, double, success, error, notification, pulse, selection)
 * @returns {boolean} - Whether the vibration was triggered
 */
export const haptic = (type = 'light') => {
  // Skip if disabled, not supported, or user prefers reduced motion
  if (!hapticEnabled || !isVibrationSupported() || prefersReducedMotion()) {
    return false;
  }
  
  const pattern = HAPTIC_PATTERNS[type] || HAPTIC_PATTERNS.light;
  
  try {
    navigator.vibrate(pattern);
    return true;
  } catch (e) {
    console.warn('[Haptic] Vibration failed:', e);
    return false;
  }
};

/**
 * Trigger custom haptic pattern
 * @param {number[]} pattern - Array of vibration durations and pauses
 */
export const hapticCustom = (pattern) => {
  if (!hapticEnabled || !isVibrationSupported() || prefersReducedMotion()) {
    return false;
  }
  
  try {
    navigator.vibrate(pattern);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Stop any ongoing vibration
 */
export const hapticStop = () => {
  if (isVibrationSupported()) {
    navigator.vibrate(0);
  }
};

// Convenience methods for common actions
export const hapticLight = () => haptic('light');
export const hapticMedium = () => haptic('medium');
export const hapticHeavy = () => haptic('heavy');
export const hapticDouble = () => haptic('double');
export const hapticSuccess = () => haptic('success');
export const hapticError = () => haptic('error');
export const hapticNotification = () => haptic('notification');
export const hapticPulse = () => haptic('pulse');
export const hapticSelection = () => haptic('selection');

// React hook for haptic feedback
export const useHaptic = () => {
  return {
    haptic,
    hapticLight,
    hapticMedium,
    hapticHeavy,
    hapticDouble,
    hapticSuccess,
    hapticError,
    hapticNotification,
    hapticPulse,
    hapticSelection,
    hapticStop,
    hapticCustom,
    isEnabled: isHapticEnabled,
    setEnabled: setHapticEnabled,
    isSupported: isVibrationSupported,
  };
};

// Initialize on module load
initHaptics();

export default {
  haptic,
  hapticLight,
  hapticMedium,
  hapticHeavy,
  hapticDouble,
  hapticSuccess,
  hapticError,
  hapticNotification,
  hapticPulse,
  hapticSelection,
  hapticStop,
  hapticCustom,
  isEnabled: isHapticEnabled,
  setEnabled: setHapticEnabled,
  isSupported: isVibrationSupported,
  initHaptics,
};
